import type React from 'react';
import { Avatar } from '../../identity/avatar';
import styles from './game-row.module.scss';

export interface GameRowProps {
  opponent: { name: string; color: string; isAI?: boolean };
  status: 'your-turn' | 'their-turn' | 'ended';
  score?: number;
  lastMove?: string;
  onClick?: () => void;
  className?: string;
}

const AIEmblemIcon: React.FC = () => (
  <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="7" width="14" height="10" rx="2" fill="var(--wh-ink-soft)" />
    <circle cx="9" cy="12" r="1.5" fill="var(--wh-bg)" />
    <circle cx="15" cy="12" r="1.5" fill="var(--wh-bg)" />
    <rect x="9" y="3" width="6" height="4" rx="1" fill="var(--wh-ink-soft)" />
    <line x1="12" y1="3" x2="12" y2="7" stroke="var(--wh-ink-soft)" strokeWidth="1.5" />
    <rect x="3" y="9" width="2" height="4" rx="1" fill="var(--wh-ink-soft)" />
    <rect x="19" y="9" width="2" height="4" rx="1" fill="var(--wh-ink-soft)" />
    <rect x="9" y="17" width="2" height="4" rx="1" fill="var(--wh-ink-soft)" />
    <rect x="13" y="17" width="2" height="4" rx="1" fill="var(--wh-ink-soft)" />
  </svg>
);

export const GameRow: React.FC<GameRowProps> = ({ opponent, status, score, lastMove, onClick, className }) => {
  const isEnded = status === 'ended';
  const isYourTurn = status === 'your-turn';
  const isInteractive = !isEnded && Boolean(onClick);

  const handleClick = () => {
    if (!isEnded && onClick) {
      onClick();
    }
  };

  const metaText = lastMove ?? (score != null ? String(score) : undefined);

  const ariaLabel = isYourTurn ? `Game against ${opponent.name}, your turn` : `Game against ${opponent.name}`;

  const avatar = (
    <Avatar
      name={opponent.name}
      color={opponent.color}
      size={36}
      emblem={opponent.isAI ? <AIEmblemIcon /> : undefined}
    />
  );

  const body = (
    <div className={styles.body}>
      <span className={styles.name}>{opponent.name}</span>
      {metaText && <span className={styles.meta}>{metaText}</span>}
    </div>
  );

  const innerClass = [
    styles.inner,
    isInteractive ? styles.innerInteractive : null,
    isYourTurn ? styles.innerYourTurn : null,
  ]
    .filter(Boolean)
    .join(' ');

  const inner = isInteractive ? (
    <button type="button" className={innerClass} aria-label={ariaLabel} onClick={handleClick}>
      {avatar}
      {body}
      {isYourTurn && <div className={styles.dot} aria-hidden="true" />}
    </button>
  ) : (
    <div className={innerClass}>
      {avatar}
      {body}
    </div>
  );

  const rowClass = [styles.gameRow, isEnded ? styles.ended : null, className].filter(Boolean).join(' ');

  return (
    <li className={rowClass} aria-disabled={isEnded ? 'true' : undefined}>
      {inner}
    </li>
  );
};
